import React from 'react';
import {
  Card,
  Row,
  Col,
  Tag,
  List,
  Statistic,
  Progress,
  Tooltip,
  Badge,
} from 'antd';
import {
  WarningOutlined,
  FireOutlined,
  UserOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import { Space } from 'antd';

// 风险点排行
interface RiskPointRank {
  name: string;
  count: number;
  trend: 'up' | 'down' | 'stable';
  percentage: number;
}

// 活跃质疑人
interface ActiveBidder {
  name: string;
  questionCount: number;
  winRate: number;
  relatedCases: number;
  frequentTopics: string[];
}

// 条款热度
interface ClauseHeat {
  keyword: string;
  count: number;
  relatedRiskPoints: string[];
}

// 模拟数据
const mockRiskPoints: RiskPointRank[] = [
  { name: '资质排他', count: 45, trend: 'up', percentage: 23 },
  { name: '参数歧义', count: 38, trend: 'up', percentage: 18 },
  { name: '业绩要求', count: 32, trend: 'stable', percentage: 15 },
  { name: '付款方式', count: 28, trend: 'down', percentage: 12 },
  { name: '工期要求', count: 25, trend: 'stable', percentage: 11 },
  { name: '低于成本价', count: 22, trend: 'up', percentage: 10 },
  { name: '人员资格', count: 18, trend: 'down', percentage: 6 },
  { name: '技术参数', count: 15, trend: 'stable', percentage: 5 },
];

const mockActiveBidders: ActiveBidder[] = [
  {
    name: '合肥科大立安安全技术有限责任公司',
    questionCount: 23,
    winRate: 60,
    relatedCases: 15,
    frequentTopics: ['资格条件', '技术参数', '人员要求'],
  },
  {
    name: '广西裕达农副产品配送有限公司',
    questionCount: 18,
    winRate: 33,
    relatedCases: 12,
    frequentTopics: ['报价评审', '中标结果'],
  },
  {
    name: '云南某某科技有限公司',
    questionCount: 15,
    winRate: 45,
    relatedCases: 10,
    frequentTopics: ['技术参数', '资格条件'],
  },
  {
    name: '昆明某某建设集团有限公司',
    questionCount: 12,
    winRate: 50,
    relatedCases: 8,
    frequentTopics: ['程序流程', '中标结果'],
  },
  {
    name: '大理某某商贸有限公司',
    questionCount: 10,
    winRate: 40,
    relatedCases: 7,
    frequentTopics: ['付款方式', '工期要求'],
  },
];

const mockClauseHeat: ClauseHeat[] = [
  { keyword: '资质等级', count: 52, relatedRiskPoints: ['资质排他', '超越资质范围'] },
  { keyword: '业绩证明', count: 48, relatedRiskPoints: ['业绩要求'] },
  { keyword: '技术参数', count: 45, relatedRiskPoints: ['参数歧义'] },
  { keyword: '付款条件', count: 38, relatedRiskPoints: ['付款方式'] },
  { keyword: '工期要求', count: 35, relatedRiskPoints: ['工期要求'] },
  { keyword: '人员资格', count: 32, relatedRiskPoints: ['人员资格'] },
  { keyword: '保证金', count: 28, relatedRiskPoints: ['低于成本价'] },
  { keyword: '评标方法', count: 25, relatedRiskPoints: ['程序流程'] },
];

const RiskMonitor: React.FC = () => {
  // 获取趋势图标
  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return <ArrowUpOutlined className="text-red-500" />;
      case 'down':
        return <ArrowDownOutlined className="text-green-500" />;
      default:
        return <span className="text-gray-400">-</span>;
    }
  };

  // 获取标签大小（词云效果）
  const getTagSize = (count: number, maxCount: number) => {
    const ratio = count / maxCount;
    if (ratio > 0.8) return { fontSize: 20, padding: '8px 16px' };
    if (ratio > 0.6) return { fontSize: 18, padding: '6px 12px' };
    if (ratio > 0.4) return { fontSize: 16, padding: '4px 10px' };
    if (ratio > 0.2) return { fontSize: 14, padding: '3px 8px' };
    return { fontSize: 12, padding: '2px 6px' };
  };

  const maxClauseCount = Math.max(...mockClauseHeat.map((c) => c.count));

  return (
    <div className="h-full overflow-hidden">
      <Row gutter={16} className="h-full">
        {/* 左侧：高频质疑点排行 */}
        <Col span={8} className="h-full overflow-auto pr-1">
          <Card
            title={
              <div className="flex items-center gap-2">
                <WarningOutlined className="text-red-500" />
                <span>高频质疑点 Top 10</span>
              </div>
            }
            className="mb-4"
           
          >
            <List
             
              dataSource={mockRiskPoints}
              renderItem={(item, index) => (
                <List.Item
                  className="hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3 w-full">
                    <Badge
                      count={index + 1}
                      style={{
                        backgroundColor:
                          index < 3 ? '#f5222d' : index < 6 ? '#faad14' : '#8c8c8c',
                      }}
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{item.name}</span>
                        <Space>
                          {getTrendIcon(item.trend)}
                          <span className="text-red-600 font-bold">{item.count}次</span>
                        </Space>
                      </div>
                      <Progress
                        percent={item.percentage}
                       
                        showInfo={false}
                        strokeColor={index < 3 ? '#f5222d' : '#faad14'}
                      />
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </Card>

          {/* 统计卡片 */}
          <Row gutter={[8, 8]}>
            <Col span={12}>
              <Card className="text-center">
                <Statistic
                  title="总质疑数"
                  value={mockRiskPoints.reduce((sum, r) => sum + r.count, 0)}
                  prefix={<WarningOutlined className="text-red-500" />}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card className="text-center">
                <Statistic
                  title="最热风险点"
                  value={mockRiskPoints[0].name}
                  valueStyle={{ fontSize: 14 }}
                  prefix={<FireOutlined className="text-orange-500" />}
                />
              </Card>
            </Col>
          </Row>
        </Col>

        {/* 中间：条款热力图 */}
        <Col span={8} className="h-full overflow-auto px-1">
          <Card
            title={
              <div className="flex items-center gap-2">
                <FireOutlined className="text-orange-500" />
                <span>招标文件条款热力图</span>
              </div>
            }
            className="mb-4"
           
          >
            <div className="min-h-[400px] p-4">
              <div className="flex flex-wrap gap-3 justify-center">
                {mockClauseHeat.map((clause) => {
                  const size = getTagSize(clause.count, maxClauseCount);
                  return (
                    <Tooltip
                      key={clause.keyword}
                      title={
                        <div>
                          <div className="font-bold">{clause.keyword}</div>
                          <div>被质疑 {clause.count} 次</div>
                          <div className="mt-1">
                            相关风险:
                            {clause.relatedRiskPoints.map((rp) => (
                              <Tag key={rp} className="ml-1 text-xs">
                                {rp}
                              </Tag>
                            ))}
                          </div>
                        </div>
                      }
                    >
                      <Tag
                        color="orange"
                        className="hover:shadow-lg transition-all"
                        style={{
                          fontSize: size.fontSize,
                          padding: size.padding,
                        }}
                      >
                        {clause.keyword}
                        <span className="ml-1 opacity-70">({clause.count})</span>
                      </Tag>
                    </Tooltip>
                  );
                })}
              </div>
            </div>

            <div className="mt-4 p-3 bg-gray-50 rounded text-xs text-gray-500">
              <div className="flex items-center gap-4 justify-center">
                <span>字号大小表示被质疑频次</span>
              </div>
            </div>
          </Card>

          {/* 条款排行 */}
          <Card title="被质疑最多条款">
            <List
             
              dataSource={mockClauseHeat.slice(0, 5)}
              renderItem={(item) => (
                <List.Item
                  className="hover:bg-gray-50"
                >
                  <div className="flex items-center justify-between w-full">
                    <Tag color="orange">{item.keyword}</Tag>
                    <span className="text-gray-500">{item.count}次</span>
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </Col>

        {/* 右侧：活跃质疑人 */}
        <Col span={8} className="h-full overflow-auto pl-1">
          <Card
            title={
              <div className="flex items-center gap-2">
                <UserOutlined className="text-blue-500" />
                <span>活跃质疑人 Top 10</span>
              </div>
            }
            className="mb-4"
           
          >
            <List
             
              dataSource={mockActiveBidders}
              renderItem={(item, index) => (
                <List.Item
                  className="hover:bg-gray-50"
                >
                  <div className="w-full">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge
                        count={index + 1}
                        style={{
                          backgroundColor:
                            index < 3 ? '#1890ff' : index < 6 ? '#52c41a' : '#8c8c8c',
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{item.name}</div>
                        <div className="text-xs text-gray-400">
                          涉及 {item.relatedCases} 个案例
                        </div>
                      </div>
                    </div>

                    <Row gutter={8} className="mb-2">
                      <Col span={8}>
                        <div className="text-center bg-blue-50 rounded p-1">
                          <div className="text-lg font-bold text-blue-600">
                            {item.questionCount}
                          </div>
                          <div className="text-xs text-gray-500">质疑数</div>
                        </div>
                      </Col>
                      <Col span={8}>
                        <div className="text-center bg-green-50 rounded p-1">
                          <div
                            className={`text-lg font-bold ${
                              item.winRate >= 60
                                ? 'text-green-600'
                                : item.winRate >= 40
                                ? 'text-orange-600'
                                : 'text-red-600'
                            }`}
                          >
                            {item.winRate}%
                          </div>
                          <div className="text-xs text-gray-500">成功率</div>
                        </div>
                      </Col>
                      <Col span={8}>
                        <div className="text-center bg-orange-50 rounded p-1">
                          <div className="text-lg font-bold text-orange-600">
                            {item.frequentTopics.length}
                          </div>
                          <div className="text-xs text-gray-500">关注领域</div>
                        </div>
                      </Col>
                    </Row>

                    <div className="flex flex-wrap gap-1">
                      {item.frequentTopics.map((topic) => (
                        <Tag key={topic} color="blue" className="text-xs">
                          {topic}
                        </Tag>
                      ))}
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </Card>

          {/* 统计 */}
          <Card>
            <div className="text-sm text-gray-500 mb-2">质疑人统计:</div>
            <Row gutter={8}>
              <Col span={12}>
                <Statistic
                  title="总质疑人"
                  value={mockActiveBidders.length}
                  valueStyle={{ fontSize: 24 }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="平均成功率"
                  value={Math.round(
                    mockActiveBidders.reduce((sum, b) => sum + b.winRate, 0) /
                      mockActiveBidders.length
                  )}
                  suffix="%"
                  valueStyle={{ fontSize: 24, color: '#52c41a' }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default RiskMonitor;
